// ◉ xlVISION · Powering Business Together!
// © ExcelVision, Lda. — Braga, Portugal
// ( ·:·●●·:· ) xlVISION | Powering Business Together!
//
// ============================================================================
// 🔴 2026-10-26 · OS DETALHES DA CÉLULA EM BLOCOS, e não numa lista corrida
// ============================================================================
//
// 📗 Cliente: *«os detalhes são uma confusão de texto surreal e layout»*. O painel mostrava 12 a 14
//    pares «rótulo: valor» seguidos, com o mesmo peso: a viatura, a viagem, o dia, as pessoas e
//    termos técnicos («registo», «nível») misturados. 📌 Agrupam-se em quatro blocos que respondem a
//    quatro perguntas — que autocarro e que dia · que viagem · o que acontece neste dia · quem
//    conduz — e um quinto para avisos e notas.
//
// ⚠️ **Nada desaparece, muda de sítio**: um rótulo que este mapa não conheça vai para «Outros», no
//    fim. E a ordem DENTRO de cada bloco é a ordem em que `detalhe()` os produziu — essa ordem já
//    foi pensada (a hora de chegada ao lado da entrada, o 2.º condutor a seguir ao motorista).
// ============================================================================

export type Par = readonly [string, string];

export interface SeccaoDoDetalhe {
  readonly titulo: string;
  readonly pares: readonly Par[];
}

const VIATURA_E_DIA = new Set(['viatura', 'subcontratado', 'data', 'dia', 'estado da viatura', 'dia de rotação']);
const VIAGEM = new Set([
  'cliente',
  'rota',
  'itinerário',
  'itinerário (por onde anda)',
  'referência da viagem',
  'a mesma viagem também em',
  'serviço',
  'registo',
  'estado',
  'fixado',
  'marcado',
  'cor',
]);
const ESTE_DIA = new Set([
  'entrada dos passageiros',
  'hora de chegada do grupo',
  'partida',
  'chegada',
  'meio',
  'local do dia',
  'localidade',
  'local presumido',
  'pernoita do motorista',
  'pernoita e alojamento',
  'base da empresa',
  'este dia',
  'sem rotação',
]);
const AVISOS = new Set(['atenção', 'nota', 'alterado', 'nível']);

function seccaoDe(rotulo: string): number {
  if (VIATURA_E_DIA.has(rotulo)) return 0;
  if (VIAGEM.has(rotulo)) return 1;
  if (ESTE_DIA.has(rotulo)) return 2;
  // Os rótulos das pessoas compõem-se na hora («motorista da viagem que PARTE», «2.º condutor»).
  if (rotulo.startsWith('motorista') || rotulo.includes('condutor')) return 3;
  if (AVISOS.has(rotulo)) return 4;
  // As horas e os locais com qualificador («partida (viagem que…)») são do dia.
  if (/^(partida|chegada|hora|entrada|pernoita|local)/.test(rotulo)) return 2;
  return 5;
}

const TITULOS = ['Viatura e dia', 'Viagem', 'Este dia', 'Quem conduz', 'Avisos e notas', 'Outros'];

/** Os pares de `detalhe()` repartidos pelos blocos, sem perder nenhum e sem mudar a ordem de cada um. */
export function seccoesDoDetalhe(pares: readonly Par[]): readonly SeccaoDoDetalhe[] {
  const grupos: Par[][] = TITULOS.map(() => []);
  for (const p of pares) grupos[seccaoDe(p[0])]?.push(p);
  return TITULOS.map((titulo, i) => ({ titulo, pares: grupos[i] ?? [] })).filter((s) => s.pares.length > 0);
}
